import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from 'environments/environment';
import { ProjectHumanResourceApiModel } from '../models/api/project-human-resource.model';

@Injectable({
  providedIn: 'root'
})
export class ProjectHumanResourceService {
  private readonly apiUrl = `${environment.apiEndPoint}/ProjectHumanResource`;

  constructor(private httpClient: HttpClient) { }

  /**
   * Gets a project human resource by ID
   * @param id The project human resource ID
   */
  getById(id: number): Observable<ProjectHumanResourceApiModel> {
    return this.httpClient.get<ProjectHumanResourceApiModel>(`${this.apiUrl}/${id}`);
  }

  /**
   * Gets all project human resources
   */
  getAll(): Observable<ProjectHumanResourceApiModel[]> {
    return this.httpClient.get<ProjectHumanResourceApiModel[]>(this.apiUrl);
  }

  /**
   * Gets project human resources by project ID
   * @param projectId The project ID
   */
  getByProjectId(projectId: number): Observable<ProjectHumanResourceApiModel[]> {
    return this.httpClient.get<ProjectHumanResourceApiModel[]>(`${this.apiUrl}/by-project/${projectId}`);
  }

  /**
   * Gets project human resources by human resource ID
   * @param humanResourceId The human resource ID
   */
  getByHumanResourceId(humanResourceId: number): Observable<ProjectHumanResourceApiModel[]> {
    return this.httpClient.get<ProjectHumanResourceApiModel[]>(`${this.apiUrl}/by-humanresource/${humanResourceId}`);
  }

  /**
   * Creates a new project human resource
   * @param model The project human resource data
   */
  create(model: ProjectHumanResourceApiModel): Observable<ProjectHumanResourceApiModel> {
    return this.httpClient.post<ProjectHumanResourceApiModel>(this.apiUrl, model);
  }

  /**
   * Creates multiple project human resources
   * @param models The project human resources data
   */
  createRange(models: ProjectHumanResourceApiModel[]): Observable<ProjectHumanResourceApiModel[]> {
    return this.httpClient.post<ProjectHumanResourceApiModel[]>(`${this.apiUrl}/range`, models);
  }

  /**
   * Updates an existing project human resource
   * @param model The project human resource data
   */
  update(model: ProjectHumanResourceApiModel): Observable<ProjectHumanResourceApiModel> {
    return this.httpClient.put<ProjectHumanResourceApiModel>(`${this.apiUrl}/${model.projectHumanResourceId}`, model);
  }

  /**
   * Updates multiple project human resources
   * @param models The project human resources data
   */
  updateRange(models: ProjectHumanResourceApiModel[]): Observable<void> {
    return this.httpClient.put<void>(`${this.apiUrl}/range`, models);
  }

  /**
   * Deletes a project human resource by ID
   * @param id The project human resource ID
   */
  delete(id: number): Observable<boolean> {
    return this.httpClient.delete<boolean>(`${this.apiUrl}/${id}`);
  }

  setStartDate(id: number, date: Date | null): Observable<ProjectHumanResourceApiModel> {
    return this.httpClient.patch<ProjectHumanResourceApiModel>(`${this.apiUrl}/${id}/start-date`, date);
  }

  setLeavingDate(id: number, date: Date | null): Observable<ProjectHumanResourceApiModel> {
    return this.httpClient.patch<ProjectHumanResourceApiModel>(`${this.apiUrl}/${id}/leaving-date`, date);
  }

  discharge(id: number, date: Date | null = null): Observable<ProjectHumanResourceApiModel> {
    return this.httpClient.patch<ProjectHumanResourceApiModel>(`${this.apiUrl}/${id}/discharge`, date ?? new Date());
  }

  /**
   * Reactivates (re-enters) a discharged assignment in place, reusing the existing
   * record instead of creating a new one. Clears the discharge/leaving date and sets
   * a fresh start date.
   * @param id The project human resource ID
   * @param date Optional re-entry (start) date; defaults to today
   */
  reactivate(id: number, date: Date | null = null): Observable<ProjectHumanResourceApiModel> {
    return this.httpClient.patch<ProjectHumanResourceApiModel>(`${this.apiUrl}/${id}/reactivate`, date ?? new Date());
  }
}